import { MusicTrack, SoundCue } from './sound-cue';

/** Where audio files are served from (`apps/client/src/assets/audio`). */
export const AUDIO_BASE_URL = '/assets/audio';

/** Gain multiplier for a cue played for an opponent's board (≈ −8 dB). */
export const DEFAULT_OPPONENT_VOLUME = 0.4;

export interface SoundCueConfig {
  /** File under `assets/audio/sfx`, without extension. */
  file: string;
  /** 0–1 base gain for this cue. */
  volume: number;
  /** Minimum gap between two plays of this cue. */
  cooldownMs?: number;
  /** Random ± playback-rate variation, so repeats don't sound identical. */
  detune?: number;
  /**
   * Which boards trigger this cue. Cues not tied to a board (UI, countdown,
   * stingers) use `own` only.
   */
  playOn: { own: boolean; opponent: boolean };
  /** Gain multiplier when played for an opponent; defaults to `DEFAULT_OPPONENT_VOLUME`. */
  opponentVolume?: number;
  /** Duck the music while this cue plays (stingers). */
  ducksMusic?: boolean;
}

const OWN = { own: true, opponent: false } as const;
const ALL = { own: true, opponent: true } as const;

/**
 * Single source of truth for every sound cue: which file it plays, how loud,
 * and on which boards. Change who hears a cue here — no other code decides it.
 */
export const SOUND_CUES: Readonly<Record<SoundCue, SoundCueConfig>> = {
  // UI
  [SoundCue.UI_CLICK]: {
    file: 'ui-click',
    volume: 0.5,
    cooldownMs: 30,
    playOn: OWN,
  },
  [SoundCue.UI_READY]: { file: 'ui-ready', volume: 0.7, playOn: OWN },
  [SoundCue.UI_UNREADY]: { file: 'ui-unready', volume: 0.6, playOn: OWN },
  [SoundCue.PLAYER_JOIN]: { file: 'player-join', volume: 0.6, playOn: OWN },
  [SoundCue.PLAYER_LEAVE]: { file: 'player-leave', volume: 0.6, playOn: OWN },
  [SoundCue.UI_ERROR]: {
    file: 'ui-error',
    volume: 0.6,
    cooldownMs: 250,
    playOn: OWN,
  },

  // Match flow
  [SoundCue.COUNTDOWN_TICK]: {
    file: 'countdown-tick',
    volume: 0.8,
    playOn: OWN,
  },
  [SoundCue.COUNTDOWN_GO]: { file: 'countdown-go', volume: 0.9, playOn: OWN },
  [SoundCue.LOW_TIME_TICK]: { file: 'low-time-tick', volume: 0.6, playOn: OWN },
  [SoundCue.SUDDEN_DEATH_ALARM]: {
    file: 'sudden-death-alarm',
    volume: 0.9,
    playOn: OWN,
  },
  [SoundCue.VICTORY]: {
    file: 'victory',
    volume: 0.9,
    playOn: OWN,
    ducksMusic: true,
  },
  [SoundCue.DEFEAT]: {
    file: 'defeat',
    volume: 0.9,
    playOn: OWN,
    ducksMusic: true,
  },

  // Food
  [SoundCue.FOOD_EAT]: {
    file: 'food-eat',
    volume: 0.6,
    cooldownMs: 40,
    detune: 0.03,
    playOn: OWN,
  },
  [SoundCue.RACING_SPAWN]: { file: 'racing-spawn', volume: 0.7, playOn: OWN },
  [SoundCue.RACING_WIN]: { file: 'racing-win', volume: 0.8, playOn: ALL },
  [SoundCue.RACING_LOST]: { file: 'racing-lost', volume: 0.6, playOn: OWN },
  [SoundCue.RACING_EXPIRE]: { file: 'racing-expire', volume: 0.5, playOn: OWN },

  // Life
  [SoundCue.DEATH]: { file: 'death', volume: 0.9, cooldownMs: 80, playOn: ALL },
  [SoundCue.RESPAWN]: { file: 'respawn', volume: 0.6, playOn: OWN },
  [SoundCue.ELIMINATED]: { file: 'eliminated', volume: 0.9, playOn: ALL },

  // Blocks
  [SoundCue.OBSTACLE_HIT]: { file: 'obstacle-hit', volume: 0.8, playOn: OWN },
  [SoundCue.BULLDOZER_SMASH]: {
    file: 'bulldozer-smash',
    volume: 0.8,
    cooldownMs: 40,
    detune: 0.03,
    playOn: OWN,
  },
  [SoundCue.CONSTRICT]: { file: 'constrict', volume: 0.8, playOn: OWN },
  [SoundCue.BLOCK_CHARGED]: { file: 'block-charged', volume: 0.5, playOn: OWN },
  [SoundCue.ATTACK_SEND]: {
    file: 'attack-send',
    volume: 0.7,
    cooldownMs: 60,
    playOn: OWN,
  },
  [SoundCue.ATTACK_INCOMING]: {
    file: 'attack-incoming',
    volume: 0.8,
    cooldownMs: 60,
    playOn: OWN,
  },
  [SoundCue.BLOCK_SLAM]: {
    file: 'block-slam',
    volume: 0.7,
    cooldownMs: 40,
    detune: 0.03,
    playOn: OWN,
  },
  [SoundCue.BLOCK_DENIED]: {
    file: 'block-denied',
    volume: 0.7,
    cooldownMs: 40,
    playOn: OWN,
  },

  // Pickups
  [SoundCue.PICKUP_COLLECT]: {
    file: 'pickup-collect',
    volume: 0.7,
    playOn: OWN,
  },
  [SoundCue.PICKUP_LOST]: { file: 'pickup-lost', volume: 0.6, playOn: OWN },
  [SoundCue.POWERUP_ACTIVATE]: {
    file: 'powerup-activate',
    volume: 0.8,
    playOn: OWN,
  },

  // Score
  [SoundCue.TITLE_GAINED]: { file: 'title-gained', volume: 0.8, playOn: ALL },
  [SoundCue.TITLE_LOST]: { file: 'title-lost', volume: 0.7, playOn: ALL },
  [SoundCue.PEAK_BONUS]: { file: 'peak-bonus', volume: 0.6, playOn: OWN },
};

export interface MusicTrackConfig {
  /** File under `assets/audio/music`, without extension. */
  file: string;
  /** 0–1 base gain for this track. */
  volume: number;
}

export const MUSIC_TRACKS: Readonly<Record<MusicTrack, MusicTrackConfig>> = {
  [MusicTrack.LOBBY]: { file: 'lobby', volume: 0.6 },
  [MusicTrack.MATCH]: { file: 'match', volume: 0.55 },
  [MusicTrack.SUDDEN_DEATH]: { file: 'sudden-death', volume: 0.6 },
};
